import type { Id, Scene } from "@supermanus/document-model";
import { formatDuration, totalSceneDuration } from "@supermanus/document-model";
import {
  useLayoutEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type Ref,
} from "react";
import { DocumentHead } from "./DocumentHead";
import { DurationField } from "./DurationField";
import { handleFieldEnterKey } from "./fieldKeyboard";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";
import { SceneHeadingLine } from "./SceneHeadingLine";
import { reorderDropClass, useSceneReorder } from "./useSceneReorder";

export interface OutlineViewProps {
  scenes: Scene[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  defaultSceneHeaderBg: string;
  onMetaChange: (
    sceneId: Id,
    patch: Partial<
      Pick<
        Scene,
        | "title"
        | "description"
        | "durationSeconds"
        | "number"
        | "headerBackground"
        | "heading"
        | "headingKind"
        | "headingLocation"
        | "headingTime"
        | "headingNote"
      >
    >,
  ) => void;
  onMoveScene?: (sceneId: Id, beforeSceneId: Id | null) => void;
  onSceneHeaderContextMenu?: (
    sceneId: Id,
    event: ReactMouseEvent,
  ) => void;
  /** Extra class on each scene card (e.g. director own/program). */
  sceneRowClass?: (sceneId: Id) => string | undefined;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
}

function AutoGrowTextarea({
  value,
  disabled,
  placeholder,
  className,
  onClick,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onClick?: (e: ReactMouseEvent<HTMLTextAreaElement>) => void;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const focusedRef = useRef(false);

  function resize() {
    const el = ref.current;
    if (!el) return;
    // Avoid height:0 — that collapses the focused field and jumps the caret
    // to the end on every keystroke.
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // While typing, keep the DOM value (uncontrolled) so a stale React
    // `value` cannot rewrite the field and yank the caret to the end.
    if (focusedRef.current) {
      resize();
      return;
    }
    if (el.value !== value) el.value = value;
    resize();
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={className}
      defaultValue={value}
      disabled={disabled}
      placeholder={placeholder}
      rows={1}
      onClick={onClick}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => {
        onChange(e.target.value);
        resize();
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        onChange(e.target.value);
        resize();
      }}
      onKeyDown={handleFieldEnterKey}
    />
  );
}

export function OutlineView({
  scenes,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  defaultSceneHeaderBg,
  onMetaChange,
  onMoveScene,
  onSceneHeaderContextMenu,
  sceneRowClass,
  showHours = true,
  showFrames = false,
  fps = 25,
}: OutlineViewProps) {
  const total = totalSceneDuration(scenes);
  const fmt = { showHours, showFrames, fps };
  const { listRef, dragSceneId, dropBeforeSceneId, beginSceneDrag } =
    useSceneReorder({
      readOnly: readOnly || !onMoveScene,
      onMoveScene: onMoveScene ?? (() => undefined),
    });

  return (
    <div className="sm-paper sm-paper-a4 sm-view-outline">
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="outline"
      />
      <div
        ref={listRef as Ref<HTMLDivElement>}
        className={`sm-outline-list${dragSceneId ? " is-reordering" : ""}`}
      >
        {scenes.map((scene, i) => {
          const dropClass = reorderDropClass(
            dragSceneId,
            dropBeforeSceneId,
            scene.id,
            i === scenes.length - 1,
          );
          return (
            <div key={scene.id} className="sm-outline-item">
              <article
                className={`sm-outline-card${
                  dragSceneId === scene.id ? " is-dragging" : ""
                }${dropClass}${
                  sceneRowClass?.(scene.id)
                    ? ` ${sceneRowClass(scene.id)}`
                    : ""
                }`}
                style={{
                  backgroundColor: sceneHeaderBg(
                    scene.headerBackground,
                    defaultSceneHeaderBg,
                  ),
                }}
                data-scene-id={scene.id}
                onContextMenu={(e) => {
                  if (readOnly || !onSceneHeaderContextMenu) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onSceneHeaderContextMenu(scene.id, e);
                }}
                title={readOnly ? undefined : "Højreklik for baggrundsfarve, dupliker, flyt, opdel eller slet"}
              >
                <div className="sm-outline-top">
                  {!readOnly && onMoveScene ? (
                    <button
                      type="button"
                      className="sm-scene-grip"
                      aria-label="Flyt scene"
                      title="Træk for at flytte scene"
                      onPointerDown={(e) => beginSceneDrag(scene.id, e)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      ⋮⋮
                    </button>
                  ) : null}
                  <SceneHeadingLine
                    sceneNumber={scene.number}
                    heading={scene.heading}
                    headingKind={scene.headingKind}
                    headingLocation={scene.headingLocation}
                    headingTime={scene.headingTime}
                    headingNote={scene.headingNote}
                    readOnly={readOnly}
                    onChange={(patch) => onMetaChange(scene.id, patch)}
                    onNumberChange={(number) =>
                      onMetaChange(scene.id, { number })
                    }
                  />
                  <DurationField
                    className="sm-scene-dur"
                    valueSeconds={scene.durationSeconds}
                    onChange={(durationSeconds) =>
                      onMetaChange(scene.id, { durationSeconds })
                    }
                    readOnly={readOnly}
                    showHours={showHours}
                    showFrames={showFrames}
                    fps={fps}
                    ariaLabel={`Varighed for scene ${scene.number}`}
                  />
                </div>
                <input
                  className="sm-outline-title"
                  value={scene.title}
                  disabled={readOnly}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    onMetaChange(scene.id, { title: e.target.value })
                  }
                  onKeyDown={handleFieldEnterKey}
                />
                <AutoGrowTextarea
                  className="sm-outline-desc"
                  value={scene.description}
                  disabled={readOnly}
                  placeholder="Scenebeskrivelse"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(description) =>
                    onMetaChange(scene.id, { description })
                  }
                />
              </article>
            </div>
          );
        })}
        <div className="sm-outline-total">
          <span>I alt</span>
          <span>{formatDuration(total, fmt)}</span>
        </div>
      </div>
    </div>
  );
}
